// src/api/index.js - VERSIÓN CORREGIDA CON VALIDACIÓN DINÁMICA DE TENANTS

/**
 * API Service - Configuración centralizada para llamadas a la API
 * @version 2.0.3
 */

// ==================== CONFIGURACIÓN DE ENTORNO ====================

const DOMAIN_MAPPING = {
  'system.jeaninformatico.com': 'api.jeaninformatico.com',
  'www.system.jeaninformatico.com': 'api.jeaninformatico.com',
  'admin.jeaninformatico.com': 'api.jeaninformatico.com',
  'app.jeaninformatico.com': 'api.jeaninformatico.com',
  'localhost': 'localhost',
  '127.0.0.1': '127.0.0.1',
  'dev.jeaninformatico.com': 'api-dev.jeaninformatico.com',
  'staging.jeaninformatico.com': 'api-staging.jeaninformatico.com',
  'test.jeaninformatico.com': 'api-test.jeaninformatico.com'
};

const ENV_CONFIG = {
  production: {
    protocol: 'https:',
    defaultApiHost: 'api.jeaninformatico.com',
    defaultApiPort: '',
    forceHttps: true,
    corsEnabled: true,
    credentials: 'omit'
  },
  staging: {
    protocol: 'https:',
    defaultApiHost: 'api-staging.jeaninformatico.com',
    defaultApiPort: '',
    forceHttps: true,
    corsEnabled: true,
    credentials: 'omit'
  },
  development: {
    protocol: 'http:',
    defaultApiHost: 'localhost',
    defaultApiPort: '5090',
    forceHttps: false,
    corsEnabled: true,
    credentials: 'omit'
  }
};

// ==================== 🔥 VALIDACIÓN DINÁMICA DE TENANTS ====================

/**
 * Tenant por defecto del sistema.
 */
const DEFAULT_TENANT = 'default';

/**
 * Valida el FORMATO del tenantId.
 * 
 * 🔥 Los tenants son DINÁMICOS (se crean en el backend), así que NO usamos whitelist.
 * 🔥 Solo validamos que tenga un formato seguro: letras, números, guiones y guiones bajos.
 * 🔥 Rechaza espacios, caracteres especiales, y longitudes > 50.
 * 
 * Ejemplos válidos:   "default", "jeaninformatica", "empresa-01", "cliente_abc"
 * Ejemplos inválidos: "Jean Informática C.A.", "empresa con espacios", "empresa@123", ""
 */
const esTenantIdValido = (tenantId) => {
  if (!tenantId || typeof tenantId !== 'string') return false;
  if (tenantId.length === 0 || tenantId.length > 50) return false;
  if (tenantId.includes(' ')) return false;
  return /^[a-zA-Z0-9_-]+$/.test(tenantId);
};

// ==================== DETECCIÓN DE ENTORNO ====================

const detectEnvironment = () => {
  if (process.env.VUE_APP_ENV) return process.env.VUE_APP_ENV;
  if (process.env.NODE_ENV === 'production') {
    if (typeof window !== 'undefined' && window.location) {
      const hostname = window.location.hostname;
      if (hostname.includes('staging') || hostname.includes('test')) return 'staging';
    }
    return 'production';
  }
  return 'development';
};

const getEnvironmentConfig = () => {
  const env = detectEnvironment();
  return ENV_CONFIG[env];
};

// ==================== CONFIGURACIÓN DE API ====================

const getApiHostname = () => {
  if (process.env.VUE_APP_API_HOST) {
    console.log('🔧 Usando API_HOST:', process.env.VUE_APP_API_HOST);
    return process.env.VUE_APP_API_HOST;
  }
  if (typeof window !== 'undefined' && window.__API_HOST__) return window.__API_HOST__;
  if (typeof window !== 'undefined' && window.location) {
    const currentHostname = window.location.hostname;
    if (DOMAIN_MAPPING[currentHostname]) return DOMAIN_MAPPING[currentHostname];
    const env = detectEnvironment();
    if (env === 'production') return ENV_CONFIG.production.defaultApiHost;
    if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
      return currentHostname;
    }
    return 'localhost';
  }
  return 'api.jeaninformatico.com';
};

const getApiPort = () => {
  if (process.env.VUE_APP_API_PORT) {
    const port = process.env.VUE_APP_API_PORT;
    return port === '443' || port === '80' ? '' : port;
  }
  if (typeof window !== 'undefined' && window.__API_PORT__) {
    const port = window.__API_PORT__;
    return port === '443' || port === '80' ? '' : port;
  }
  const envConfig = getEnvironmentConfig();
  const apiHost = getApiHostname();
  if (apiHost === 'api.jeaninformatico.com' || apiHost.includes('jeaninformatico.com')) {
    return '';
  }
  return envConfig.defaultApiPort;
};

const getApiProtocol = () => {
  if (process.env.VUE_APP_API_PROTOCOL) {
    let protocol = process.env.VUE_APP_API_PROTOCOL;
    if (!protocol.endsWith(':')) protocol += ':';
    return protocol;
  }
  if (typeof window !== 'undefined' && window.__API_PROTOCOL__) {
    let protocol = window.__API_PROTOCOL__;
    if (!protocol.endsWith(':')) protocol += ':';
    return protocol;
  }
  const envConfig = getEnvironmentConfig();
  const apiHost = getApiHostname();
  if (apiHost.includes('jeaninformatico.com') && envConfig.forceHttps) {
    return 'https:';
  }
  return envConfig.protocol;
};

const buildApiUrl = () => {
  const protocol = getApiProtocol();
  const hostname = getApiHostname();
  const port = getApiPort();
  let url = `${protocol}//${hostname}`;
  if (port && port !== '') url += `:${port}`;
  return url;
};

// ==================== VARIABLES GLOBALES ====================

const API_URL = buildApiUrl();
const API_HOSTNAME = getApiHostname();
const API_PORT = getApiPort();
const API_PROTOCOL = getApiProtocol();
const ENVIRONMENT = detectEnvironment();

console.group('🚀 API Service Initialized');
console.log('📊 Environment:', ENVIRONMENT);
console.log('🔗 API URL:', API_URL);
console.log('🏠 API Hostname:', API_HOSTNAME);
console.log('🔌 API Port:', API_PORT || '443 (default)');
console.log('🔒 Protocol:', API_PROTOCOL);
console.groupEnd();

// ==================== CONFIGURACIÓN DE SERVIDORES ====================

const CONFIG = {
  servers: [
    {
      id: 'primary',
      name: 'API Principal',
      baseUrl: API_URL,
      apiUrl: API_URL,
      isDefault: true,
      available: true
    }
  ],
  currentServer: null,
  getDefaultServer() { return this.servers[0]; }
};

const STORAGE_KEY = 'api_server_config_v2';

// ==================== 🔥 FUNCIONES DE TENANT (VALIDACIÓN DINÁMICA) ====================

/**
 * Obtiene el tenant actual de forma SEGURA.
 * 
 * 🔥 Valida solo el FORMATO (no whitelist) porque los tenants son dinámicos.
 * 🔥 Usa una sola clave: 'tenantId'.
 */
export const getCurrentTenant = () => {
  try {
    // 🔥 UNA SOLA CLAVE: tenantId
    let tenantId = localStorage.getItem('tenantId') || DEFAULT_TENANT;

    // 🔥 Validar formato
    if (!esTenantIdValido(tenantId)) {
      console.warn(`⚠️ Tenant '${tenantId}' con formato inválido. Usando '${DEFAULT_TENANT}'.`);
      tenantId = DEFAULT_TENANT;
      localStorage.setItem('tenantId', tenantId);
    }

    return tenantId;
  } catch (e) {
    console.error('Error leyendo tenantId:', e);
    return DEFAULT_TENANT;
  }
};

/**
 * Establece el tenant actual.
 * 
 * 🔥 Valida el FORMATO antes de guardar.
 * 🔥 Guarda en UNA SOLA CLAVE: 'tenantId'.
 */
export const setCurrentTenant = (tenantId) => {
  try {
    if (!esTenantIdValido(tenantId)) {
      console.warn(`⚠️ Tenant '${tenantId}' con formato inválido. Guardando '${DEFAULT_TENANT}'.`);
      tenantId = DEFAULT_TENANT;
    }

    localStorage.setItem('tenantId', tenantId);
    console.log('🏢 Tenant ID guardado:', tenantId);
  } catch (e) {
    console.error('Error guardando tenantId:', e);
  }
};

/**
 * Limpia el tenant actual y la configuración asociada.
 */
export const clearTenant = () => {
  localStorage.removeItem('tenantId');
  localStorage.removeItem('tenantConfig');
  localStorage.removeItem('empresasDisponibles');
  // Compatibilidad: limpiar también la clave antigua si existe
  localStorage.removeItem('selectedTenantId');
  console.log('🧹 Tenant limpiado');
};

/**
 * Resetea el tenant a 'default'.
 */
export const resetTenant = () => {
  localStorage.setItem('tenantId', DEFAULT_TENANT);
  localStorage.removeItem('selectedTenantId');
  console.log('🔄 Tenant reset a default');
};

// ==================== FUNCIONES DE AUTENTICACIÓN ====================

export const getToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      console.error('Error al parsear usuario almacenado');
      return null;
    }
  }
  return null;
};

export const setAuth = (token, user, remember = false) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('isAuthenticated', 'true');
  localStorage.setItem('auth_timestamp', Date.now().toString());

  if (remember) {
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('user', JSON.stringify(user));
    sessionStorage.setItem('isAuthenticated', 'true');
  }

  // 🔥 Guardar tenant SOLO si es válido (setCurrentTenant ya valida el formato)
  if (user && user.tenantId) {
    setCurrentTenant(user.tenantId);
  }

  console.log('🔐 Autenticación guardada:');
  console.log('   - Token:', !!localStorage.getItem('token'));
  console.log('   - User:', !!localStorage.getItem('user'));
  console.log('   - isAuthenticated:', localStorage.getItem('isAuthenticated'));
  console.log('   - Tenant:', getCurrentTenant());

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-change', {
      detail: { authenticated: true, user }
    }));
  }
};

export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('user_data');
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('auth_timestamp');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('isAuthenticated');
  clearTenant();
  console.log('🧹 Autenticación limpiada');
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-change', {
      detail: { authenticated: false, user: null }
    }));
  }
};

export const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    return payload;
  } catch (error) {
    console.error('Error decodificando token:', error);
    return null;
  }
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = decodeToken(token);
    if (!payload || !payload.exp) return true;
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true;
  }
};

export const getTokenRemainingTime = (token) => {
  if (!token) return 0;
  try {
    const payload = decodeToken(token);
    if (!payload || !payload.exp) return 0;
    const now = Math.floor(Date.now() / 1000);
    const remaining = payload.exp - now;
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
};

export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;

  if (isTokenExpired(token)) {
    console.warn('⚠️ Token expirado, limpiando sesión');
    clearAuth();
    return false;
  }

  const user = getCurrentUser();
  if (!user) {
    console.warn('⚠️ No usuario encontrado, limpiando sesión');
    clearAuth();
    return false;
  }

  const authFlag = localStorage.getItem('isAuthenticated') || sessionStorage.getItem('isAuthenticated');
  if (authFlag !== 'true') {
    console.warn('⚠️ Flag de autenticación incorrecto, corrigiendo...');
    localStorage.setItem('isAuthenticated', 'true');
  }

  return true;
};

export const requireAuth = (redirectPath = '/login') => {
  if (!isAuthenticated() && typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    const loginUrl = redirectPath === '/login'
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : redirectPath;
    if (currentPath !== '/login') {
      window.location.href = loginUrl;
      return false;
    }
  }
  return true;
};

// ==================== FUNCIONES DE SERVIDOR ====================

const fixServerConfig = (server) => {
  if (!server) return server;
  const fixedServer = { ...server };
  const currentApiUrl = buildApiUrl();
  fixedServer.baseUrl = currentApiUrl;
  fixedServer.apiUrl = currentApiUrl;
  return fixedServer;
};

const testServer = async (server) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const fixedServer = fixServerConfig(server);
    console.log(`🔍 Testing server: ${fixedServer.name} (${fixedServer.baseUrl})`);
    const response = await fetch(`${fixedServer.baseUrl}/health`, {
      method: 'GET',
      signal: controller.signal,
      headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
      mode: 'cors'
    });
    clearTimeout(timeoutId);
    if (response.ok) {
      console.log(`✅ Server available: ${fixedServer.name}`);
      return { ...fixedServer, available: true };
    }
    console.warn(`⚠️ Server health check failed: ${fixedServer.name}`);
    return { ...fixedServer, available: false };
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn(`❌ Server unavailable: ${server.name} - ${error.message}`);
    return { ...server, available: false };
  }
};

export const initializeAPI = async () => {
  console.log('🔄 Initializing API Service...');
  const currentConfig = {
    id: 'primary',
    name: `API Server (${API_HOSTNAME})`,
    baseUrl: API_URL,
    apiUrl: API_URL,
    isDefault: true
  };
  const fixedConfig = fixServerConfig(currentConfig);
  const testResult = await testServer(fixedConfig);
  if (testResult.available) {
    CONFIG.currentServer = testResult;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testResult));
    console.log(`✅ API initialized successfully: ${testResult.baseUrl}`);
    return testResult;
  }
  console.warn(`⚠️ API server may be offline, using configured URL: ${fixedConfig.baseUrl}`);
  CONFIG.currentServer = fixedConfig;
  return fixedConfig;
};

// ==================== FUNCIÓN PRINCIPAL API FETCH ====================

export const apiFetch = async (endpoint, options = {}) => {
  let retryCount = 0;
  const maxRetries = 2;
  const retryDelay = 1000;

  const executeRequest = async () => {
    try {
      const token = getToken();
      if (token && isTokenExpired(token)) {
        console.warn('⚠️ Token expired, clearing session');
        clearAuth();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
      }

      if (!CONFIG.currentServer) await initializeAPI();
      if (!CONFIG.currentServer || !CONFIG.currentServer.apiUrl) {
        throw new Error('API server configuration is invalid');
      }

      const server = fixServerConfig(CONFIG.currentServer);

      let normalizedEndpoint = endpoint;
      if (!normalizedEndpoint.startsWith('/api/') && !normalizedEndpoint.startsWith('api/')) {
        normalizedEndpoint = normalizedEndpoint.startsWith('/')
          ? `/api${normalizedEndpoint}`
          : `/api/${normalizedEndpoint}`;
      } else if (normalizedEndpoint.startsWith('api/')) {
        normalizedEndpoint = `/${normalizedEndpoint}`;
      }

      const url = `${server.apiUrl}${normalizedEndpoint}`;

      // 🔥 HEADERS CON TENANT VALIDADO (solo formato)
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'X-Tenant-ID': getCurrentTenant(),   // ← SIEMPRE validado por formato
        ...options.headers
      };

      if (token) headers['Authorization'] = `Bearer ${token}`;

      const fetchOptions = {
        method: options.method || 'GET',
        headers,
        mode: 'cors',
        credentials: ENVIRONMENT === 'production' ? 'same-origin' : 'omit',
        ...options
      };

      if (fetchOptions.body && typeof fetchOptions.body !== 'string' && !(fetchOptions.body instanceof FormData)) {
        fetchOptions.body = JSON.stringify(fetchOptions.body);
      }
      if (fetchOptions.body instanceof FormData) {
        delete fetchOptions.headers['Content-Type'];
      }

      if (ENVIRONMENT !== 'production') {
        console.log(`📡 API Request: ${fetchOptions.method} ${url}`);
        console.log('📨 Headers:', headers);
      }

      const response = await fetch(url, fetchOptions);

      if (response.status === 401) {
        console.warn('🔐 Unauthorized request, clearing session');
        clearAuth();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        throw new Error('Unauthorized. Please login again.');
      }

      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.error === 'LICENSE_EXPIRED') {
          console.error('🚨 License expired');
          if (typeof window !== 'undefined' && window.location.pathname !== '/blocked') {
            window.location.href = '/blocked';
          }
          throw new Error(`System blocked: ${errorData.message || 'License expired'}`);
        }
      }

      if (response.ok) {
        if (response.status === 204) return null;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return await response.json();
        }
        return await response.text();
      }

      let errorMessage = `Error ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat().join(', ');
          errorMessage = `${errorData.title || 'Validation error'}: ${validationErrors}`;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.mensaje) {
          errorMessage = errorData.mensaje;
        }
      } catch {
        const textError = await response.text();
        if (textError) errorMessage = textError;
      }
      throw new Error(errorMessage);

    } catch (error) {
      const isNetworkError = error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError') ||
        error.name === 'TypeError' ||
        error.message.includes('ERR_CONNECTION_REFUSED') ||
        error.message.includes('ERR_CONNECTION_TIMED_OUT') ||
        error.message.includes('load failed');

      const isAuthError = error.message.includes('401') ||
        error.message.includes('Unauthorized') ||
        error.message.includes('Session expired');

      if (isAuthError) throw error;

      if (isNetworkError && retryCount < maxRetries) {
        retryCount++;
        console.warn(`🔄 Retrying request (${retryCount}/${maxRetries})...`);
        CONFIG.currentServer = null;
        localStorage.removeItem(STORAGE_KEY);
        await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount));
        await initializeAPI();
        return executeRequest();
      }

      if (isNetworkError) {
        throw new Error(`Cannot connect to API server at ${API_URL}. Please verify the server is running.`);
      }
      throw error;
    }
  };

  return executeRequest();
};

// ==================== FUNCIONES DE UTILIDAD ====================

export const getCurrentApiUrl = () => {
  if (!CONFIG.currentServer) return API_URL;
  return fixServerConfig(CONFIG.currentServer).apiUrl;
};

export const getCurrentServer = () => {
  if (!CONFIG.currentServer) return null;
  return fixServerConfig(CONFIG.currentServer);
};

export const getCurrentImagesUrl = () => {
  const baseUrl = getCurrentApiUrl();
  return `${baseUrl}/imagenes`;
};

export const getImageUrl = (imageName, options = {}) => {
  const { width, height, quality, fallback } = options;
  if (!imageName || imageName.trim() === '') {
    console.warn('getImageUrl: Empty image name');
    return fallback || '';
  }
  if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
    return imageName;
  }
  const baseUrl = getCurrentImagesUrl();
  let cleanImage = imageName.trim();
  if (cleanImage.startsWith('/imagenes/')) cleanImage = cleanImage.substring(9);
  else if (cleanImage.startsWith('imagenes/')) cleanImage = cleanImage.substring(8);
  else if (cleanImage.startsWith('/')) cleanImage = cleanImage.substring(1);
  let finalUrl = `${baseUrl}/${encodeURIComponent(cleanImage)}`;
  const params = new URLSearchParams();
  if (width) params.append('w', width);
  if (height) params.append('h', height);
  if (quality) params.append('q', quality);
  const queryString = params.toString();
  if (queryString) finalUrl += `?${queryString}`;
  return finalUrl;
};

export const getServersList = () => CONFIG.servers.map(fixServerConfig);

export const setServer = async (serverId) => {
  let server = CONFIG.servers.find(s => s.id === serverId);
  if (!server) throw new Error(`Server ${serverId} not found`);
  server = fixServerConfig(server);
  const testResult = await testServer(server);
  if (testResult.available) {
    CONFIG.currentServer = testResult;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(testResult));
    console.log(`✅ Server changed to: ${server.name}`);
    return testResult;
  } else {
    throw new Error(`Server ${server.name} is not available at ${server.baseUrl}`);
  }
};

export const checkStatus = async () => {
  try {
    if (!CONFIG.currentServer) await initializeAPI();
    const server = fixServerConfig(CONFIG.currentServer);
    const testResult = await testServer(server);
    const authenticated = isAuthenticated();
    const user = getCurrentUser();
    const tenant = getCurrentTenant();
    return {
      server,
      available: testResult.available,
      authenticated,
      user,
      tenant,
      imagesUrl: `${server.baseUrl}/imagenes`,
      tokenRemaining: authenticated && getToken() ? getTokenRemainingTime(getToken()) : 0,
      environment: ENVIRONMENT,
      apiUrl: API_URL,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    return {
      server: CONFIG.currentServer,
      available: false,
      authenticated: false,
      error: error.message,
      environment: ENVIRONMENT,
      apiUrl: API_URL,
      timestamp: new Date().toISOString()
    };
  }
};

export const checkAllServers = async () => {
  const fixedServers = CONFIG.servers.map(fixServerConfig);
  const testPromises = fixedServers.map(server => testServer(server));
  const results = await Promise.all(testPromises);
  return results.map(result => ({
    id: result.id,
    name: result.name,
    url: result.baseUrl,
    available: result.available
  }));
};

export const forceCorrectPort = () => {
  console.log(`🔧 Forcing API configuration...`);
  CONFIG.servers = CONFIG.servers.map(fixServerConfig);
  if (CONFIG.currentServer) CONFIG.currentServer = fixServerConfig(CONFIG.currentServer);
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const fixed = fixServerConfig(parsed);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fixed));
    }
  } catch (error) {
    console.warn('Error updating localStorage:', error);
  }
  console.log(`✅ API configuration forced: ${API_URL}`);
};

export const debugAPI = () => {
  const fixedServers = CONFIG.servers.map(fixServerConfig);
  const fixedCurrent = CONFIG.currentServer ? fixServerConfig(CONFIG.currentServer) : null;
  const token = getToken();
  const authenticated = isAuthenticated();
  const user = getCurrentUser();
  const tenant = getCurrentTenant();

  console.group('🔧 API Debug Information');
  console.log('📊 Environment:', ENVIRONMENT);
  console.log('🌐 Configuration:');
  console.log('   - API URL:', API_URL);
  console.log('   - Hostname:', API_HOSTNAME);
  console.log('   - Port:', API_PORT || '443');
  console.log('   - Protocol:', API_PROTOCOL);
  console.log('   - Current Location:', typeof window !== 'undefined' ? window.location.href : 'N/A');
  console.log('');
  console.log('🏢 Tenant:');
  console.log('   - Current Tenant:', tenant);
  console.log('   - tenantId:', localStorage.getItem('tenantId'));
  console.log('   - selectedTenantId (legacy):', localStorage.getItem('selectedTenantId'));
  console.log('');
  console.log('🔐 Authentication:');
  console.log('   - Authenticated:', authenticated);
  console.log('   - User:', user);
  console.log('   - Token:', token ? `${token.substring(0, 30)}...` : 'No token');
  if (token) console.log('   - Token remaining:', getTokenRemainingTime(token), 'seconds');
  console.log('');
  console.log('🖥️ Server:');
  console.log('   - Current:', fixedCurrent);
  console.log('   - Images URL:', fixedCurrent ? `${fixedCurrent.baseUrl}/imagenes` : 'Not configured');
  console.groupEnd();

  return {
    environment: ENVIRONMENT,
    config: { url: API_URL, hostname: API_HOSTNAME, port: API_PORT, protocol: API_PROTOCOL },
    tenant,
    auth: { authenticated, user, hasToken: !!token, tokenRemaining: token ? getTokenRemainingTime(token) : 0 },
    current: fixedCurrent,
    imagesUrl: fixedCurrent ? `${fixedCurrent.baseUrl}/imagenes` : null
  };
};

// ==================== 🔥 INICIALIZACIÓN AUTOMÁTICA ====================

if (typeof window !== 'undefined') {
  // 🔥 Limpiar tenants con FORMATO INVÁLIDO al arranque
  try {
    const stored = localStorage.getItem('tenantId') || localStorage.getItem('selectedTenantId');
    if (stored && !esTenantIdValido(stored)) {
      console.log(`🧹 Limpiando tenant con formato inválido: '${stored}'`);
      localStorage.removeItem('tenantId');
      localStorage.removeItem('selectedTenantId');
      localStorage.removeItem('tenantConfig');
      localStorage.setItem('tenantId', DEFAULT_TENANT);
    }
    // 🔥 Migración: si existe 'selectedTenantId' (clave antigua) pero no 'tenantId', migrar
    if (!localStorage.getItem('tenantId') && localStorage.getItem('selectedTenantId')) {
      const old = localStorage.getItem('selectedTenantId');
      if (esTenantIdValido(old)) {
        localStorage.setItem('tenantId', old);
      }
      localStorage.removeItem('selectedTenantId');
    }
  } catch (e) {
    console.error('Error limpiando tenants inválidos:', e);
  }

  forceCorrectPort();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        initializeAPI().catch(error => console.error('Failed to initialize API:', error));
      }, 100);
    });
  } else {
    setTimeout(() => {
      initializeAPI().catch(error => console.error('Failed to initialize API:', error));
    }, 100);
  }

  setInterval(() => {
    const token = getToken();
    if (token && isTokenExpired(token)) {
      console.warn('Token expired, clearing session');
      clearAuth();
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }, 300000);
}

// ==================== EXPORTACIONES ====================

export default {
  getToken,
  getCurrentUser,
  setAuth,
  clearAuth,
  decodeToken,
  isTokenExpired,
  getTokenRemainingTime,
  isAuthenticated,
  requireAuth,
  getCurrentTenant,
  setCurrentTenant,
  clearTenant,
  resetTenant,
  initializeAPI,
  apiFetch,
  getCurrentApiUrl,
  getCurrentServer,
  getCurrentImagesUrl,
  getImageUrl,
  getServersList,
  setServer,
  checkStatus,
  checkAllServers,
  forceCorrectPort,
  debugAPI,
  config: CONFIG,
  environment: ENVIRONMENT,
  apiUrl: API_URL
};